import {
  FEE_POLICY_VERSION,
  ORGANIZER_COMMISSION_BPS,
  ORGANIZER_SHARE_BPS,
  PRICING_DISCLOSURE_VERSION,
  RACER_SERVICE_FEE_BPS,
  TAX_DISCLOSURE_VERSION,
  TAX_INTEGRATION_VERSION,
  TAX_POLICY_VERSION,
  calculateOrganizerPayoutPreview,
} from "../constants/organizerPayoutTerms";
import type {
  Race,
  RegistrationPricingSnapshot,
  RegistrationTaxPolicy,
  RegistrationTaxPolicyComponent,
} from "../types/iosModels";
import {
  formatStripeMoney,
  getStripeChargeAmountQuantum,
  getStripeMinorUnitMultiplier,
  isStripeSupportedPresentmentCurrency,
} from "./stripeCurrencies";

export type RegistrationPricingPreview = ReturnType<
  typeof calculateOrganizerPayoutPreview
> & {
  currency: string;
  baseEntryAmount: number;
  racerServiceFeeAmount: number;
  totalAmount: number;
  platformCommissionAmount: number;
  organizerTransferAmount: number;
  platformRetainedAmount: number;
  currencyExponent: number;
  organizerCommissionBps: number;
  organizerShareBps: number;
  racerServiceFeeBps: number;
  isFree: boolean;
  source: "server" | "preview";
  taxPolicy: RegistrationTaxPolicy | null;
  isPayable: boolean;
  pricingError: string | null;
};

export type RegistrationPricingAcknowledgement = {
  pricingDisclosureVersion: string;
  feePolicyVersion: string;
  totalAmount: number;
  currency: string;
};

const normalizeCurrency = (currency: string | null | undefined): string => {
  const normalized = currency?.trim().toUpperCase();
  return normalized && /^[A-Z]{3}$/.test(normalized) ? normalized : "EUR";
};

const TAX_LIABLE_PARTIES = new Set(["organizer", "rauxa"]);
const TAXABILITY_VALUES = new Set([
  "not_taxable",
  "organizer_determined",
  "taxable",
]);
const TAX_CONFIGURATION_STATUSES = new Set([
  "ready",
  "rauxa_collection_required",
  "tax_calculation_required",
]);
const TAX_POLICY_REVIEW_STATUSES = new Set([
  "reviewed",
  "pending_review",
  "blocked",
]);
const TAX_REGISTRATION_RULE_MODES = new Set([
  "required",
  "not_required",
  "manual_review",
]);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asNonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

const asSafeInteger = (value: unknown): number | null =>
  typeof value === "number" && Number.isSafeInteger(value) ? value : null;

const parseTaxPolicyComponent = (
  value: unknown
): RegistrationTaxPolicyComponent | null => {
  const record = asRecord(value);
  const liableParty = asNonEmptyString(record?.liableParty);
  const taxability = asNonEmptyString(record?.taxability);
  const priceIncludesTax = record?.priceIncludesTax;
  const rawTaxCode = record?.taxCode;
  if (
    !record ||
    !liableParty ||
    !TAX_LIABLE_PARTIES.has(liableParty) ||
    !taxability ||
    !TAXABILITY_VALUES.has(taxability) ||
    typeof priceIncludesTax !== "boolean" ||
    (rawTaxCode !== undefined &&
      rawTaxCode !== null &&
      typeof rawTaxCode !== "string")
  ) {
    return null;
  }

  return {
    liableParty,
    taxability,
    priceIncludesTax,
    taxCode:
      typeof rawTaxCode === "string" && rawTaxCode.trim()
        ? rawTaxCode
        : null,
  };
};

export const parseTaxRegistrationRule = (
  value: unknown
): NonNullable<RegistrationTaxPolicy["registrationRule"]> | null => {
  if (value === undefined || value === null) {
    return { mode: "required", requiredTypes: [] };
  }
  const record = asRecord(value);
  const mode = asNonEmptyString(record?.mode);
  const requiredTypes = record?.requiredTypes;
  if (
    !record ||
    !mode ||
    !TAX_REGISTRATION_RULE_MODES.has(mode) ||
    !Array.isArray(requiredTypes) ||
    requiredTypes.some((entry) => !asNonEmptyString(entry))
  ) {
    return null;
  }

  let requiredRegistrations: NonNullable<
    RegistrationTaxPolicy["registrationRule"]
  >["requiredRegistrations"];
  if (Object.prototype.hasOwnProperty.call(record, "requiredRegistrations")) {
    if (!Array.isArray(record.requiredRegistrations)) {
      return null;
    }
    requiredRegistrations = [];
    const seen = new Set<string>();
    for (const value of record.requiredRegistrations) {
      const descriptor = asRecord(value);
      const type = asNonEmptyString(descriptor?.type)?.trim().toLowerCase();
      // Stripe jurisdiction identifiers are opaque: retain case and leading zeros.
      const jurisdiction = asNonEmptyString(descriptor?.jurisdiction)?.trim();
      if (
        !descriptor ||
        !type ||
        !jurisdiction ||
        Object.keys(descriptor).some(
          (key) => key !== "type" && key !== "jurisdiction"
        )
      ) {
        return null;
      }
      const key = JSON.stringify([type, jurisdiction]);
      if (!seen.has(key)) {
        seen.add(key);
        requiredRegistrations.push({ type, jurisdiction });
      }
    }
  }
  if (
    mode !== "required" &&
    (requiredTypes.length > 0 || (requiredRegistrations?.length ?? 0) > 0)
  ) {
    return null;
  }
  return {
    mode,
    requiredTypes: Array.from(
      new Set(requiredTypes.map((entry) => String(entry).trim().toLowerCase()))
    ),
    ...(requiredRegistrations === undefined ? {} : { requiredRegistrations }),
  };
};

export const parseCompleteRegistrationTaxPolicy = (
  value: unknown
): RegistrationTaxPolicy | null => {
  const record = asRecord(value);
  const version = asNonEmptyString(record?.version);
  const integrationVersion = asNonEmptyString(record?.integrationVersion);
  const disclosureVersion = asNonEmptyString(record?.disclosureVersion);
  const jurisdiction = asNonEmptyString(record?.jurisdiction);
  const determinationSource = asNonEmptyString(record?.determinationSource);
  const configurationStatus = asNonEmptyString(record?.configurationStatus);
  const reviewStatus =
    asNonEmptyString(record?.reviewStatus) || "reviewed";
  const registrationRule = parseTaxRegistrationRule(record?.registrationRule);
  const raceEntry = parseTaxPolicyComponent(record?.raceEntry);
  const serviceFee = parseTaxPolicyComponent(record?.serviceFee);
  if (
    !record ||
    !version ||
    version !== TAX_POLICY_VERSION ||
    integrationVersion !== TAX_INTEGRATION_VERSION ||
    disclosureVersion !== TAX_DISCLOSURE_VERSION ||
    !jurisdiction ||
    !determinationSource ||
    !configurationStatus ||
    !TAX_CONFIGURATION_STATUSES.has(configurationStatus) ||
    !TAX_POLICY_REVIEW_STATUSES.has(reviewStatus) ||
    reviewStatus !== "reviewed" ||
    !registrationRule ||
    !raceEntry ||
    !serviceFee ||
    raceEntry.liableParty !== serviceFee.liableParty ||
    raceEntry.priceIncludesTax ||
    serviceFee.priceIncludesTax
  ) {
    return null;
  }

  return {
    version,
    integrationVersion,
    disclosureVersion,
    jurisdiction,
    determinationSource,
    configurationStatus,
    reviewStatus,
    registrationRule,
    raceEntry,
    serviceFee,
  };
};

const roundedBasisPointsAmount = (
  amount: number,
  basisPoints: number,
  quantum: number
): number =>
  Math.round((amount * basisPoints) / 10_000 / quantum) * quantum;

export const isCurrentRegistrationPricingSnapshot = (
  snapshot: RegistrationPricingSnapshot | null | undefined
): snapshot is RegistrationPricingSnapshot => {
  if (!snapshot) {
    return false;
  }

  const expectedCurrency = snapshot.currency.trim().toUpperCase();
  const expectedCurrencyExponent = Math.round(
    Math.log10(getStripeMinorUnitMultiplier(expectedCurrency))
  );
  const monetaryAmounts = [
    snapshot.baseEntryAmount,
    snapshot.racerServiceFeeAmount,
    snapshot.totalAmount,
    snapshot.platformCommissionAmount,
    snapshot.organizerTransferAmount,
    snapshot.platformRetainedAmount,
  ];
  if (
    snapshot.feePolicyVersion !== FEE_POLICY_VERSION ||
    snapshot.pricingDisclosureVersion !== PRICING_DISCLOSURE_VERSION ||
    snapshot.currency !== expectedCurrency ||
    !isStripeSupportedPresentmentCurrency(expectedCurrency) ||
    snapshot.currencyExponent !== expectedCurrencyExponent ||
    snapshot.organizerCommissionBps !== ORGANIZER_COMMISSION_BPS ||
    snapshot.platformFeeBps !== ORGANIZER_COMMISSION_BPS ||
    snapshot.organizerShareBps !== ORGANIZER_SHARE_BPS ||
    snapshot.racerServiceFeeBps !== RACER_SERVICE_FEE_BPS ||
    !Number.isSafeInteger(snapshot.currencyExponent) ||
    monetaryAmounts.some(
      (amount) => !Number.isSafeInteger(amount) || amount < 0
    ) ||
    snapshot.baseEntryAmount <= 0 ||
    snapshot.baseEntryAmount > Number.MAX_SAFE_INTEGER / 10_000 ||
    !parseCompleteRegistrationTaxPolicy(snapshot.taxPolicy)
  ) {
    return false;
  }

  const quantum = getStripeChargeAmountQuantum(expectedCurrency);
  const expectedServiceFeeAmount = roundedBasisPointsAmount(
    snapshot.baseEntryAmount,
    RACER_SERVICE_FEE_BPS,
    quantum
  );
  const expectedCommissionAmount = roundedBasisPointsAmount(
    snapshot.baseEntryAmount,
    ORGANIZER_COMMISSION_BPS,
    quantum
  );
  return (
    snapshot.baseEntryAmount % quantum === 0 &&
    snapshot.racerServiceFeeAmount === expectedServiceFeeAmount &&
    snapshot.platformCommissionAmount === expectedCommissionAmount &&
    snapshot.totalAmount ===
      snapshot.baseEntryAmount + snapshot.racerServiceFeeAmount &&
    snapshot.organizerTransferAmount ===
      snapshot.baseEntryAmount - snapshot.platformCommissionAmount &&
    snapshot.platformRetainedAmount ===
      snapshot.platformCommissionAmount + snapshot.racerServiceFeeAmount
  );
};

export const parseCurrentRegistrationPricingSnapshot = (
  value: unknown
): RegistrationPricingSnapshot | null => {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const feePolicyVersion = asNonEmptyString(record.feePolicyVersion);
  const pricingDisclosureVersion = asNonEmptyString(
    record.pricingDisclosureVersion
  );
  const currency = asNonEmptyString(record.currency);
  const taxPolicy = parseCompleteRegistrationTaxPolicy(record.taxPolicy);
  const currencyExponent = asSafeInteger(record.currencyExponent);
  const baseEntryAmount = asSafeInteger(record.baseEntryAmount);
  const racerServiceFeeBps = asSafeInteger(record.racerServiceFeeBps);
  const racerServiceFeeAmount = asSafeInteger(record.racerServiceFeeAmount);
  const totalAmount = asSafeInteger(record.totalAmount);
  const organizerCommissionBps = asSafeInteger(record.organizerCommissionBps);
  const platformFeeBps = asSafeInteger(record.platformFeeBps);
  const platformCommissionAmount = asSafeInteger(
    record.platformCommissionAmount
  );
  const organizerShareBps = asSafeInteger(record.organizerShareBps);
  const organizerTransferAmount = asSafeInteger(record.organizerTransferAmount);
  const platformRetainedAmount = asSafeInteger(record.platformRetainedAmount);
  if (
    !feePolicyVersion ||
    !pricingDisclosureVersion ||
    !currency ||
    !taxPolicy ||
    currencyExponent === null ||
    baseEntryAmount === null ||
    racerServiceFeeBps === null ||
    racerServiceFeeAmount === null ||
    totalAmount === null ||
    organizerCommissionBps === null ||
    platformFeeBps === null ||
    platformCommissionAmount === null ||
    organizerShareBps === null ||
    organizerTransferAmount === null ||
    platformRetainedAmount === null
  ) {
    return null;
  }

  const snapshot: RegistrationPricingSnapshot = {
    feePolicyVersion,
    pricingDisclosureVersion,
    currency,
    currencyExponent,
    baseEntryAmount,
    racerServiceFeeBps,
    racerServiceFeeAmount,
    totalAmount,
    organizerCommissionBps,
    platformFeeBps,
    platformCommissionAmount,
    organizerShareBps,
    organizerTransferAmount,
    platformRetainedAmount,
    taxPolicy,
  };
  return isCurrentRegistrationPricingSnapshot(snapshot) ? snapshot : null;
};

const taxPolicyComponentsMatch = (
  left: RegistrationTaxPolicyComponent,
  right: RegistrationTaxPolicyComponent
): boolean =>
  left.liableParty === right.liableParty &&
  left.taxability === right.taxability &&
  left.priceIncludesTax === right.priceIncludesTax &&
  (left.taxCode ?? null) === (right.taxCode ?? null);

export const registrationPricingSnapshotsMatch = (
  left: RegistrationPricingSnapshot,
  right: RegistrationPricingSnapshot
): boolean => {
  const leftTaxPolicy = parseCompleteRegistrationTaxPolicy(left.taxPolicy);
  const rightTaxPolicy = parseCompleteRegistrationTaxPolicy(right.taxPolicy);
  return Boolean(
    isCurrentRegistrationPricingSnapshot(left) &&
      isCurrentRegistrationPricingSnapshot(right) &&
      left.feePolicyVersion === right.feePolicyVersion &&
      left.pricingDisclosureVersion === right.pricingDisclosureVersion &&
      left.currency === right.currency &&
      left.currencyExponent === right.currencyExponent &&
      left.baseEntryAmount === right.baseEntryAmount &&
      left.racerServiceFeeBps === right.racerServiceFeeBps &&
      left.racerServiceFeeAmount === right.racerServiceFeeAmount &&
      left.totalAmount === right.totalAmount &&
      left.organizerCommissionBps === right.organizerCommissionBps &&
      left.platformFeeBps === right.platformFeeBps &&
      left.platformCommissionAmount === right.platformCommissionAmount &&
      left.organizerShareBps === right.organizerShareBps &&
      left.organizerTransferAmount === right.organizerTransferAmount &&
      left.platformRetainedAmount === right.platformRetainedAmount &&
      leftTaxPolicy &&
      rightTaxPolicy &&
      leftTaxPolicy.version === rightTaxPolicy.version &&
      leftTaxPolicy.integrationVersion === rightTaxPolicy.integrationVersion &&
      leftTaxPolicy.disclosureVersion === rightTaxPolicy.disclosureVersion &&
      leftTaxPolicy.jurisdiction === rightTaxPolicy.jurisdiction &&
      leftTaxPolicy.determinationSource ===
        rightTaxPolicy.determinationSource &&
      leftTaxPolicy.configurationStatus ===
        rightTaxPolicy.configurationStatus &&
      leftTaxPolicy.reviewStatus === rightTaxPolicy.reviewStatus &&
      leftTaxPolicy.registrationRule?.mode ===
        rightTaxPolicy.registrationRule?.mode &&
      JSON.stringify(leftTaxPolicy.registrationRule?.requiredTypes || []) ===
        JSON.stringify(rightTaxPolicy.registrationRule?.requiredTypes || []) &&
      JSON.stringify(
        leftTaxPolicy.registrationRule?.requiredRegistrations || []
      ) ===
        JSON.stringify(
          rightTaxPolicy.registrationRule?.requiredRegistrations || []
        ) &&
      taxPolicyComponentsMatch(leftTaxPolicy.raceEntry, rightTaxPolicy.raceEntry) &&
      taxPolicyComponentsMatch(leftTaxPolicy.serviceFee, rightTaxPolicy.serviceFee)
  );
};

export const getRaceRegistrationPricingPreview = (
  race: Pick<Race, "event" | "pricing">
): RegistrationPricingPreview => {
  const currency = normalizeCurrency(race.event.currency);
  const isFree = race.event.entryType === "Free";
  const baseEntryPrice = isFree ? 0 : Number(race.event.price);
  const preview = calculateOrganizerPayoutPreview(baseEntryPrice, currency);
  const currencyMultiplier = getStripeMinorUnitMultiplier(currency);
  const chargeAmountQuantum = getStripeChargeAmountQuantum(currency);
  const currencyExponent = Math.round(Math.log10(currencyMultiplier));
  const snapshot = race.pricing;
  const rawBaseEntryAmount =
    (Number.isFinite(baseEntryPrice) && baseEntryPrice > 0 ? baseEntryPrice : 0) *
    currencyMultiplier;
  const expectedBaseEntryAmount = Math.round(rawBaseEntryAmount);
  const baseAmountUsesSupportedPrecision =
    isFree ||
    (baseEntryPrice > 0 &&
      Math.abs(rawBaseEntryAmount - expectedBaseEntryAmount) <= 1e-7 &&
      expectedBaseEntryAmount % chargeAmountQuantum === 0);
  const expectedRacerServiceFeeAmount = Math.round(
    preview.racerServiceFee * currencyMultiplier
  );
  const expectedTotalAmount = Math.round(
    preview.racerTotal * currencyMultiplier
  );
  const expectedPlatformCommissionAmount = Math.round(
    preview.organizerCommission * currencyMultiplier
  );
  const expectedOrganizerTransferAmount = Math.round(
    preview.organizerShare * currencyMultiplier
  );
  const expectedPlatformRetainedAmount = Math.round(
    preview.platformRetained * currencyMultiplier
  );
  const snapshotIsValid = Boolean(
    !isFree &&
      baseAmountUsesSupportedPrecision &&
      snapshot &&
      isCurrentRegistrationPricingSnapshot(snapshot) &&
      snapshot.currency === currency &&
      snapshot.currencyExponent === currencyExponent &&
      snapshot.baseEntryAmount === expectedBaseEntryAmount
  );

  if (snapshotIsValid && snapshot) {
    const snapshotMultiplier = 10 ** snapshot.currencyExponent;
    return {
      entryFee: snapshot.baseEntryAmount / snapshotMultiplier,
      organizerCommission:
        snapshot.platformCommissionAmount / snapshotMultiplier,
      corsaFee: snapshot.platformCommissionAmount / snapshotMultiplier,
      organizerShare: snapshot.organizerTransferAmount / snapshotMultiplier,
      racerServiceFee: snapshot.racerServiceFeeAmount / snapshotMultiplier,
      racerTotal: snapshot.totalAmount / snapshotMultiplier,
      platformRetained: snapshot.platformRetainedAmount / snapshotMultiplier,
      currency,
      baseEntryAmount: snapshot.baseEntryAmount,
      racerServiceFeeAmount: snapshot.racerServiceFeeAmount,
      totalAmount: snapshot.totalAmount,
      platformCommissionAmount: snapshot.platformCommissionAmount,
      organizerTransferAmount: snapshot.organizerTransferAmount,
      platformRetainedAmount: snapshot.platformRetainedAmount,
      currencyExponent: snapshot.currencyExponent,
      organizerCommissionBps: snapshot.organizerCommissionBps,
      organizerShareBps: snapshot.organizerShareBps,
      racerServiceFeeBps: snapshot.racerServiceFeeBps,
      isFree,
      source: "server",
      taxPolicy: snapshot.taxPolicy ?? null,
      isPayable: true,
      pricingError: null,
    };
  }

  return {
    ...preview,
    currency,
    baseEntryAmount: Math.round(preview.entryFee * currencyMultiplier),
    racerServiceFeeAmount: expectedRacerServiceFeeAmount,
    totalAmount: expectedTotalAmount,
    platformCommissionAmount: expectedPlatformCommissionAmount,
    organizerTransferAmount: expectedOrganizerTransferAmount,
    platformRetainedAmount: expectedPlatformRetainedAmount,
    currencyExponent,
    organizerCommissionBps: ORGANIZER_COMMISSION_BPS,
    organizerShareBps: ORGANIZER_SHARE_BPS,
    racerServiceFeeBps: RACER_SERVICE_FEE_BPS,
    isFree,
    source: "preview",
    taxPolicy: null,
    isPayable: isFree,
    pricingError: isFree
      ? null
      : "Registration pricing is unavailable. Please contact the organizer.",
  };
};

export const getRacePricingAcknowledgement = (
  race: Pick<Race, "event" | "pricing">
): RegistrationPricingAcknowledgement => {
  const preview = getRaceRegistrationPricingPreview(race);

  if (!preview.isPayable || (!preview.isFree && preview.source !== "server")) {
    throw new Error(
      preview.pricingError ||
        "Registration pricing is unavailable. Please contact the organizer."
    );
  }

  return {
    pricingDisclosureVersion: PRICING_DISCLOSURE_VERSION,
    feePolicyVersion: FEE_POLICY_VERSION,
    totalAmount: preview.totalAmount,
    currency: preview.currency,
  };
};

export const formatRegistrationMoney = (
  amount: number,
  currency: string,
  locale?: string,
  currencyExponent?: number
): string =>
  formatStripeMoney(
    amount,
    normalizeCurrency(currency),
    locale,
    currencyExponent
  );
